import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import InboxIcon from '@mui/icons-material/Inbox';
import { useTranslation } from 'react-i18next';
import { collection, getDocs } from 'firebase/firestore';
import type { CapCertification, NetworkAffiliation } from '@dosfilos/domain';
import { networkApi } from '../../lib/api/network.api';
import { db } from '../../lib/firebase';
import { errorText } from '../../lib/error-text';
import { useTenantStore } from '../../stores/tenant.store';
import { NetworkQueueRow } from './components/NetworkQueueRow';
import { RevokeCertificationDialog } from './components/RevokeCertificationDialog';
import {
  MEMBERSHIP_LADDER,
  capToQueueItem,
  centerCards,
  decidedQueue,
  filterQueue,
  ladderProgress,
  membershipToQueueItem,
  openQueue,
  summarize,
  conditionsDue,
  type QueueFilter,
  type QueueItem,
} from '../../lib/network-dashboard';
import { formatDate } from '../../lib/i18n-format';

// ─── El tablero del revisor de TMAI ─────────────────────────────────────────
//
// Sigue leyendo EXCLUSIVAMENTE el tenant de la red: las rondas por los dos
// callables, y las afiliaciones y certificaciones directo de Firestore, que las
// reglas abren al rol `networkAuditor`. Ningún tenant de seminario se toca.
//
// QUÉ CAMBIÓ RESPECTO DE LA BANDEJA ANTERIOR. Listaba tres bloques nombrados por
// su tipo interno y dejaba fuera lo que más pesa: una certificación otorgada no
// aparecía en NINGUNA pantalla de la red, y la fila de una propuesta CAP era la
// única sin estado, sin antigüedad y sin botón para abrirla. Acá manda una sola
// pregunta —qué espera decisión y desde cuándo— y el resto es contexto: quién es
// cada centro, qué se certificó y qué se decidió antes.

interface CertificationRow extends CapCertification {
  programName?: string;
}

export function NetworkHomePage() {
  const { t } = useTranslation('sis');
  const navigate = useNavigate();
  const { currentTenantId } = useTenantStore();
  const tenantId = currentTenantId ?? '';

  const [items, setItems] = useState<QueueItem[] | null>(null);
  const [affiliations, setAffiliations] = useState<NetworkAffiliation[]>([]);
  const [certifications, setCertifications] = useState<CertificationRow[]>([]);
  const [revoking, setRevoking] = useState<CertificationRow | null>(null);
  /** Umbrales de aviso del tenant de la red. Ausentes → NO se avisa: inventar un
   *  número acá sería afirmar una política de servicio que TMAI no fijó. */
  const [noticeDays, setNoticeDays] = useState<{ formal?: number; advisory?: number }>();
  const [filter, setFilter] = useState<QueueFilter>({});
  const [error, setError] = useState('');
  /** Un solo instante para toda la pantalla: dos relojes distintos darían dos
   *  antigüedades distintas para la misma ronda. */
  const [now] = useState(() => new Date());

  const load = useCallback(async () => {
    if (!tenantId) return;
    setError('');
    try {
      // `allSettled`: que falte el registro de certificaciones no puede dejar al
      // revisor sin su bandeja.
      const [membership, cap, affiliationDocs, certificationDocs] = await Promise.allSettled([
        networkApi.listSubmissions({ tenantId }),
        networkApi.listCapSubmissions({ tenantId }),
        getDocs(collection(db, `tenants/${tenantId}/networkAffiliations`)),
        getDocs(collection(db, `tenants/${tenantId}/capCertifications`)),
      ]);
      if (membership.status === 'rejected') throw membership.reason;

      const capRows = cap.status === 'fulfilled' ? cap.value.submissions : [];
      // El nombre del programa vive en la ronda que lo presentó; la certificación
      // guarda el id, que sobrevive a un renombre.
      const programNames = new Map(capRows.map((r) => [r.programId, r.programName]));

      setItems([
        ...membership.value.submissions.map(membershipToQueueItem),
        ...capRows.map(capToQueueItem),
      ]);
      setNoticeDays(membership.value.noticeDays);
      setAffiliations(
        affiliationDocs.status === 'fulfilled'
          ? affiliationDocs.value.docs.map((d) => d.data() as NetworkAffiliation)
          : [],
      );
      setCertifications(
        certificationDocs.status === 'fulfilled'
          ? certificationDocs.value.docs.map((d) => {
              const c = d.data() as CapCertification;
              const name = programNames.get(c.programId);
              return { ...c, ...(name ? { programName: name } : {}) };
            })
          : [],
      );
    } catch (e) {
      setError(errorText(e));
      setItems([]);
    }
  }, [tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const all = useMemo(() => items ?? [], [items]);
  const summary = useMemo(
    () => summarize({ items: all, affiliations, certifications, noticeDays, now }),
    [all, affiliations, certifications, noticeDays, now],
  );
  const dueConditions = useMemo(() => conditionsDue(certifications, now), [certifications, now]);
  const centers = useMemo(
    () => centerCards({ affiliations, items: all, certifications }),
    [affiliations, all, certifications],
  );
  // Un filtro sólo ofrece lo que existe en los datos: en una red nueva, "sin
  // tipo declarado" no tendría nunca resultados y obliga a preguntar qué es.
  const subjectsPresent = useMemo(() => new Set(all.map((i) => i.subject)), [all]);
  const kindsPresent = useMemo(() => new Set(all.map((i) => i.kind)), [all]);
  const pending = useMemo(() => openQueue(filterQueue(all, filter)), [all, filter]);
  const decided = useMemo(() => decidedQueue(filterQueue(all, filter)), [all, filter]);

  if (!items) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const date = (iso: string) => formatDate(iso, { dateStyle: 'medium' });

  const kpi = (value: number, label: string, sub: string, attention = false) => (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 3,
        flex: '1 1 190px',
        ...(attention && value > 0 ? { borderColor: 'warning.main' } : {}),
      }}
    >
      <Typography
        sx={{
          fontSize: 34,
          fontWeight: 600,
          lineHeight: 1,
          fontVariantNumeric: 'tabular-nums',
          color: attention && value > 0 ? 'warning.main' : 'text.primary',
        }}
      >
        {value}
      </Typography>
      <Typography variant="body2" sx={{ mt: 1 }}>
        {label}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {sub}
      </Typography>
    </Paper>
  );

  const stageSummary = MEMBERSHIP_LADDER.filter((s) => summary.centersByStage[s])
    .map(
      (s) => `${summary.centersByStage[s]} ${t(`stages.${s}`, { ns: 'membership' }).toLowerCase()}`,
    )
    .join(' · ');

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {t('network.dashboard.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('network.dashboard.subtitle')}
          </Typography>
        </Box>
        <Button onClick={() => void load()}>{t('network.inbox.refresh')}</Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
        {kpi(
          summary.pending,
          t('network.dashboard.kpi.pending'),
          t('network.dashboard.kpi.pendingSub', {
            formal: summary.pendingFormal,
            advisory: summary.pendingAdvisory,
          }),
          true,
        )}
        {kpi(
          summary.overdue,
          t('network.dashboard.kpi.overdue'),
          noticeDays?.formal
            ? t('network.dashboard.kpi.overdueSub', { days: noticeDays.formal })
            : t('network.dashboard.kpi.overdueNoThreshold'),
          true,
        )}
        {kpi(
          summary.activeCenters,
          t('network.dashboard.kpi.centers'),
          stageSummary || t('network.dashboard.kpi.centersNone'),
        )}
        {kpi(
          summary.certificationsGranted,
          t('network.dashboard.kpi.certifications'),
          summary.certificationsRevoked > 0
            ? t('network.dashboard.kpi.certificationsRevoked', { n: summary.certificationsRevoked })
            : t('network.dashboard.kpi.certificationsNoneRevoked'),
        )}
      </Stack>

      {/* LAS CONDICIONES VENCIDAS, ARRIBA DE LA BANDEJA. Una certificación
          condicionada sigue vigente y nada se suspende solo; lo único que la
          hace real es que vuelva a ponerse delante de una persona y envejezca a
          la vista hasta que alguien decida. */}
      {dueConditions.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {t('network.dashboard.conditionsDue.title', { n: dueConditions.length })}
          </Typography>
          <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2.5 }}>
            {dueConditions.map((c) => (
              <li key={c.id}>
                <Typography variant="body2">
                  {t('network.dashboard.conditionsDue.row', {
                    program: c.programName ?? c.programId,
                    cap: c.capCode,
                    date: date(c.conditions!.reviewBy),
                    n: c.conditions!.items.length,
                  })}
                </Typography>
              </li>
            ))}
          </Box>
        </Alert>
      )}

      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        {t('network.dashboard.queue.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {t('network.dashboard.queue.help')}
      </Typography>

      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        <TextField
          select
          size="small"
          label={t('network.dashboard.filter.subject')}
          value={filter.subject ?? 'all'}
          onChange={(e) =>
            setFilter((f) => ({
              ...f,
              subject: e.target.value as NonNullable<QueueFilter['subject']>,
            }))
          }
          sx={{ minWidth: 170 }}
        >
          <MenuItem value="all">{t('network.dashboard.filter.all')}</MenuItem>
          {(['cap', 'membership'] as const)
            .filter((s) => subjectsPresent.has(s))
            .map((s) => (
              <MenuItem key={s} value={s}>
                {t(`network.dashboard.subject.${s}`)}
              </MenuItem>
            ))}
        </TextField>
        <TextField
          select
          size="small"
          label={t('network.dashboard.filter.kind')}
          value={filter.kind ?? 'all'}
          onChange={(e) =>
            setFilter((f) => ({ ...f, kind: e.target.value as NonNullable<QueueFilter['kind']> }))
          }
          sx={{ minWidth: 170 }}
        >
          <MenuItem value="all">{t('network.dashboard.filter.all')}</MenuItem>
          {(['formal', 'advisory', 'legacy'] as const)
            .filter((k) => kindsPresent.has(k))
            .map((k) => (
              <MenuItem key={k} value={k}>
                {t(`network.dashboard.kind.${k}`)}
              </MenuItem>
            ))}
        </TextField>
        <TextField
          select
          size="small"
          label={t('network.dashboard.filter.center')}
          value={filter.seminaryTenantId ?? 'all'}
          onChange={(e) => setFilter((f) => ({ ...f, seminaryTenantId: e.target.value }))}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="all">{t('network.dashboard.filter.all')}</MenuItem>
          {centers.map((c) => (
            <MenuItem key={c.seminaryTenantId} value={c.seminaryTenantId}>
              {c.seminaryName}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Paper variant="outlined" sx={{ borderRadius: 3, mb: 3 }}>
        {pending.length === 0 ? (
          <Stack spacing={1.5} alignItems="center" sx={{ py: 5, textAlign: 'center' }}>
            <InboxIcon sx={{ fontSize: 44, opacity: 0.35 }} />
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480 }}>
              {t('network.dashboard.queue.empty')}
            </Typography>
          </Stack>
        ) : (
          <Stack divider={<Divider />}>
            {pending.map((item) => (
              <NetworkQueueRow
                key={item.id}
                item={item}
                now={now}
                noticeDays={noticeDays}
                onOpen={() => navigate(item.href)}
              />
            ))}
          </Stack>
        )}
      </Paper>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={3}
        alignItems="flex-start"
        sx={{ mb: 3 }}
      >
        <Paper variant="outlined" sx={{ borderRadius: 3, flex: 1.3, width: '100%' }}>
          <Box sx={{ p: 2, pb: 1.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t('network.dashboard.certifications.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('network.dashboard.certifications.help')}
            </Typography>
          </Box>
          <Divider />
          {certifications.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              {t('network.dashboard.certifications.empty')}
            </Typography>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('network.dashboard.certifications.program')}</TableCell>
                    <TableCell>{t('network.dashboard.certifications.cap')}</TableCell>
                    <TableCell>{t('network.dashboard.certifications.since')}</TableCell>
                    <TableCell>{t('network.dashboard.certifications.state')}</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {certifications.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell>
                        {c.programName ?? c.programId}
                        <Typography variant="caption" color="text.secondary" display="block">
                          {centers.find((x) => x.seminaryTenantId === c.seminaryTenantId)
                            ?.seminaryName ?? c.seminaryTenantId}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {c.capCode} · {t(`network.inbox.cap.level.${c.level}`)}
                      </TableCell>
                      <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {date(c.grantedAt)}
                      </TableCell>
                      <TableCell>
                        {/* Una certificación CONDICIONADA está vigente, pero no
                            debe lucir igual que una limpia: quien mira el
                            registro necesita ver que hay algo pendiente y
                            cuándo vuelve a mirarse. */}
                        <Chip
                          size="small"
                          color={
                            c.status !== 'granted' ? 'error' : c.conditions ? 'warning' : 'success'
                          }
                          label={
                            c.status !== 'granted'
                              ? t('network.dashboard.certifications.revoked', {
                                  date: c.revokedAt ? date(c.revokedAt) : '—',
                                })
                              : c.conditions
                                ? t('network.dashboard.certifications.conditional', {
                                    date: date(c.conditions.reviewBy),
                                  })
                                : t('network.dashboard.certifications.granted')
                          }
                        />
                      </TableCell>
                      <TableCell align="right">
                        {/* Sólo sobre una VIGENTE: el dominio rechaza revocar
                            dos veces, y ofrecer el botón igual sería prometer
                            una acción que va a fallar. */}
                        {c.status === 'granted' && (
                          <Button size="small" color="error" onClick={() => setRevoking(c)}>
                            {t('network.dashboard.certifications.revoke')}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ borderRadius: 3, flex: 1, width: '100%' }}>
          <Box sx={{ p: 2, pb: 1.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t('network.dashboard.centers.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('network.dashboard.centers.help')}
            </Typography>
          </Box>
          <Divider />
          {centers.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              {t('network.dashboard.centers.empty')}
            </Typography>
          ) : (
            <Stack divider={<Divider />}>
              {centers.map((c) => (
                <Box key={c.seminaryTenantId} sx={{ p: 2 }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="baseline"
                    justifyContent="space-between"
                  >
                    <Typography sx={{ fontWeight: 600 }}>{c.seminaryName}</Typography>
                    <Chip
                      size="small"
                      color={c.affiliationStatus === 'active' ? 'default' : 'error'}
                      label={
                        c.affiliationStatus === 'active'
                          ? t(`stages.${c.stage}`, { ns: 'membership' })
                          : t('network.dashboard.centers.revoked')
                      }
                    />
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={(ladderProgress(c.stage) / MEMBERSHIP_LADDER.length) * 100}
                    sx={{ mt: 1, height: 6, borderRadius: 3 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {MEMBERSHIP_LADDER.map((s) => t(`stages.${s}`, { ns: 'membership' })).join(
                      ' · ',
                    )}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                    {c.openRounds > 0 && (
                      <Chip
                        size="small"
                        color="info"
                        label={t('network.dashboard.centers.openRounds', { n: c.openRounds })}
                      />
                    )}
                    {c.certifications > 0 && (
                      <Chip
                        size="small"
                        color="success"
                        variant="outlined"
                        label={t('network.dashboard.centers.certifications', {
                          n: c.certifications,
                        })}
                      />
                    )}
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </Paper>
      </Stack>

      <RevokeCertificationDialog
        open={revoking !== null}
        onClose={() => setRevoking(null)}
        onRevoked={() => void load()}
        tenantId={tenantId ?? ''}
        certification={
          revoking
            ? {
                id: revoking.id,
                programName: revoking.programName ?? revoking.programId,
                capCode: revoking.capCode,
                centerName:
                  centers.find((x) => x.seminaryTenantId === revoking.seminaryTenantId)
                    ?.seminaryName ?? revoking.seminaryTenantId,
              }
            : null
        }
      />

      {decided.length > 0 && (
        <>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('network.dashboard.history.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('network.dashboard.history.help')}
          </Typography>
          <Paper variant="outlined" sx={{ borderRadius: 3 }}>
            <Stack divider={<Divider />}>
              {decided.map((item) => (
                <NetworkQueueRow
                  key={item.id}
                  item={item}
                  now={now}
                  noticeDays={noticeDays}
                  onOpen={() => navigate(item.href)}
                />
              ))}
            </Stack>
          </Paper>
        </>
      )}
    </Box>
  );
}
